'use client';

import { useCallback } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import { IWallet, TokenFaucet, DRIFT_PROGRAM_ID } from '@drift-labs/sdk';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { COMMON_UI_UTILS } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getSubAccountStorageKey } from 'src/utils/account';
import { useFetchAndSubscribeSubAccounts } from '../user/useFetchAndSubscribeSubaccounts/useFetchAndSubscribeSubaccounts';
import { useSyncDeletedSubaccountsPubkey } from '../user/useFetchAndSubscribeSubaccounts/useSyncDeletedSubaccounts';
import useDriftClient from '../useDriftClient';

export const useLinkWallet = () => {
	const driftClient = useDriftClient();
	const getDriftStore = useDriftStore((s) => s.get);
	const connection = useDriftStore((s) => s.connection.current);

	const setDriftStore = useDriftStore((s) => s.set);
	const accountsSet = useDriftAccountsStore((s) => s.set);
	const resetAccountsState = useDriftAccountsStore((s) => s.resetAccountsState);

	const { fetchAndSubscribeSubAccounts } = useFetchAndSubscribeSubAccounts();
	const { syncDeletedSubaccountsPubkey } = useSyncDeletedSubaccountsPubkey();

	const linkWalletToDriftClient = useCallback(async () => {
		if (!driftClient) return;

		// Skip regular connection logic when using account emulation
		const urlSearchParams = new URLSearchParams(window.location.search);

		if (
			urlSearchParams.has('authority') ||
			urlSearchParams.has('userAccount')
		) {
			return;
		}

		// we reset the accounts state first, so that account state dependencies are able to react to the state reset.
		// this prevents a race condition where the account state still exists in the store, but the user client is unsubscribed,
		// causing data access errors (especially in User.getUserAccount())
		resetAccountsState();
		await Promise.all(driftClient.unsubscribeUsers());
		// we reset the account state again, in case there is a race condition that updates the account state just before the
		// users are fully unsubscribed
		resetAccountsState();

		const wallet = getDriftStore().wallet;
		const authority = wallet.current.adapter?.publicKey as PublicKey;

		const connectionStateForWallet = wallet.connectionState;

		const subAccountStorageKey = getSubAccountStorageKey(authority, Env.sdkEnv);
		const storedSubAccountIdValue = subAccountStorageKey
			? (window?.localStorage?.getItem(subAccountStorageKey) ?? undefined)
			: undefined;

		const subAccountId = storedSubAccountIdValue
			? Number(storedSubAccountIdValue)
			: undefined;

		const success = await driftClient.updateWallet(
			wallet.current.adapter as IWallet,
			undefined,
			subAccountId,
			true,
			undefined
		);

		if (!success) {
			console.log('Error subscribing to users for new wallet');
		}

		const mockUsdcFaucet = new TokenFaucet(
			connection as Connection,
			wallet.current.adapter as IWallet,
			new PublicKey(DRIFT_PROGRAM_ID),
			new PublicKey(DEFAULT_PUBLIC_KEY.toString())
		);

		// add faucet to state
		setDriftStore((state) => {
			state.faucet = mockUsdcFaucet;
		});

		let userAccountExists = false;

		try {
			fetchAndSubscribeSubAccounts();
			syncDeletedSubaccountsPubkey(authority as PublicKey);

			let initialAccountId = driftClient?.activeSubAccountId;
			const initialAuthority = driftClient?.authority;

			try {
				// we can do this to determine if user exists instead of doing await user.exists() since we only just fetched subaccounts above
				userAccountExists = !!driftClient
					?.getUser(initialAccountId)
					.getUserAccount();
			} catch (err: any) {
				userAccountExists = false;
			}

			// ensure that the user account exists before setting the user key
			const authorityHasDriftUsers = driftClient.getUsers().length > 0;
			if (!userAccountExists && authorityHasDriftUsers) {
				const firstUser = driftClient.getUsers()[0];
				initialAccountId = firstUser.getUserAccount().subAccountId;
			}

			const userKey = COMMON_UI_UTILS.getUserKey(
				initialAccountId,
				initialAuthority
			);

			accountsSet((s) => {
				s.currentUserKey = userKey;
			});
		} catch (err: any) {
			// user account doesn't exist
			// TODO : I think this isn't true anymore - it gracefully handles there being no user account and we should let the error bubble if we encounter one here.
		}

		const authorityString = authority?.toString();

		connectionStateForWallet?.progress('ClientConnected');
		connectionStateForWallet?.progress('AdapterConnected');

		setDriftStore((s) => {
			s.wallet.connectionState = connectionStateForWallet;
			// @ts-ignore
			s.wallet.currentPublicKeyString = authorityString;
		});

		connectionStateForWallet.progress('SubaccountsSubscribed');
	}, [
		driftClient,
		connection,
		getDriftStore,
		setDriftStore,
		accountsSet,
		resetAccountsState,
		fetchAndSubscribeSubAccounts,
		syncDeletedSubaccountsPubkey,
	]);

	return {
		linkWalletToDriftClient,
	};
};
